import React, { lazy, useEffect, useState } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { ColorModeContext, useMode } from "./theme";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { createTheme } from "./themes";
import { PrivateRoutes, PublicRoutes } from "./models/routes";
import { AuthGuard } from "./guards";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { decodeOperatorJWT } from "./services/auth.service";
import { loadOperatorCompanyClient } from "./services/user.service";
import { createUser, resetUser } from "./redux/states/user";
import { updateToken, resetToken } from "./redux/states/token";
import { Loader } from "./components/molecules/Loader";
import { AxiosInterceptor } from "./utilities/axios.interceptors";
import { createVersion } from "./redux/states/versionBE";
import { PageRecoverPassword } from "./components/organisms/RecoverPassword/PageRecoverPassword";
import { LanguageProvider } from "./context/LanguageContext";

const LoginPage = lazy(() => import("./pages/login/index"));
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const SsoBridge = lazy(() => import("./pages/sso-bridge"));

AxiosInterceptor();

export const App = () => {
  const [colorMode] = useMode();
  const theme = createTheme();
  const tokenStore = useSelector((store) => store.token);
  const dispatch = useDispatch();
  const [loading, setLoadig] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("appDarkMode");
    const legacy = localStorage.getItem("operativeDarkMode");
    const isDark = stored !== null ? stored === "true" : legacy === "true";
    const root = document.documentElement;
    if (isDark) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, []);

  useEffect(() => {
    if (tokenStore && tokenStore?.token) {
      const userDecode = decodeOperatorJWT(tokenStore.token);
      const isExpired =
        !userDecode?.exp ||
        userDecode.exp <= Math.floor(Date.now() / 1000);
      if (isExpired) {
        // Si el token persistido ya venció, limpiar estado para evitar el loop
        // de requests autenticados que devuelven 401 y vuelven a /login.
        dispatch(resetUser());
        dispatch(resetToken());
        setLoadig(false);
        return;
      }
      dispatch(
        createUser({
          ...userDecode,
          roles: userDecode.company_client ? "company_client" : userDecode.role,
        })
      );
      dispatch(updateToken({ token: tokenStore.token }));
      dispatch(
        createVersion(
          userDecode.version_code ? userDecode.version_code : "1.0.0"
        )
      );
      loadOperatorCompanyClient(userDecode, dispatch);
      setLoadig(false);
    } else {
      setLoadig(false);
    }
  }, []);

  if (loading) return <Loader title="Cargando..." />;

  return (
    <LanguageProvider>
      <div className="app">
        <ColorModeContext.Provider value={colorMode}>
          <ThemeProvider theme={theme}>
            <CssBaseline>
              {/* {userState?.id ? (
              <>
                <Sidebar />
                <main className="content">
                  <Topbar />
                  <RoutesWithNotFound>
                    <Route path="/" element={<Navigate to={PrivateRoutes.DASHBOARD} />} />
                    <Route path={PublicRoutes.LOGIN} element={<LoginPage />} />
                    <Route element={<AuthGuard privateValidation={true} />}>
                      <Route path={`/*`} element={<Dashboard />} />
                    </Route>
                  </RoutesWithNotFound>
                </main>
              </>
            ) :
              <Routes>
                <Route path="*" element={<Navigate to={PublicRoutes.LOGIN} />} />
                <Route path={PublicRoutes.LOGIN} element={<LoginPage />} />
              </Routes>
            } */}
              <Routes>
                <Route
                  path="/"
                  element={<Navigate to={PrivateRoutes.DASHBOARD} />}
                />
                <Route path={PublicRoutes.LOGIN} element={<LoginPage />} />
                <Route path="/sso-bridge" element={<SsoBridge />} />
                <Route
                  path="/reset-password"
                  element={<PageRecoverPassword />}
                />
                <Route element={<AuthGuard privateValidation={true} />}>
                  <Route
                    path={`${PrivateRoutes.DASHBOARD}/*`}
                    element={<Dashboard />}
                  />
                </Route>
                <Route
                  path="*"
                  element={<Navigate to={`/${PublicRoutes.LOGIN}`} />}
                />
              </Routes>
            </CssBaseline>
          </ThemeProvider>
        </ColorModeContext.Provider>
      </div>
    </LanguageProvider>
  );
};
